import { useState, useEffect } from 'react';
import { Info, CheckCircle, AlertTriangle, AlertOctagon, X, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const TYPE_STYLES = {
  info: {
    bg: 'bg-sky-50 border-sky-200',
    icon: Info,
    iconColor: 'text-sky-600',
    textColor: 'text-sky-900',
    btnColor: 'bg-sky-600 hover:bg-sky-700 text-white',
  },
  success: {
    bg: 'bg-emerald-50 border-emerald-200',
    icon: CheckCircle,
    iconColor: 'text-emerald-600',
    textColor: 'text-emerald-900',
    btnColor: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    textColor: 'text-amber-900',
    btnColor: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  urgent: {
    bg: 'bg-red-50 border-red-200',
    icon: AlertOctagon,
    iconColor: 'text-red-600',
    textColor: 'text-red-900',
    btnColor: 'bg-red-600 hover:bg-red-700 text-white',
  },
};

/**
 * Dashboard notice banner — loads the active banner for the given target.
 * @param {{ target: 'tradesperson' | 'employer' }} props
 */
export default function DashboardBanner({ target }) {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const storageKey = target === 'tradesperson'
    ? 'dismissed_trades_banner_version'
    : 'dismissed_employer_banner_version';

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/dashboard-banner/${target}`);
        if (!data.active) return;

        const savedVersion = localStorage.getItem(storageKey);
        if (savedVersion === data.version) {
          setDismissed(true);
          return;
        }
        setBanner(data);
      } catch {
        // Fail silently — dashboard must still render
      }
    };
    fetchBanner();
  }, [target, storageKey]);

  if (!banner || dismissed) return null;

  const style = TYPE_STYLES[banner.banner_type] || TYPE_STYLES.info;
  const Icon = style.icon;

  const handleDismiss = () => {
    localStorage.setItem(storageKey, banner.version);
    setDismissed(true);
  };

  return (
    <div
      className={`relative border rounded-lg px-4 py-3 mb-6 flex items-start gap-3 ${style.bg}`}
      data-testid={`dashboard-banner-${target}`}
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${style.iconColor}`} />

      <div className="flex-1 min-w-0">
        {banner.title && (
          <p className={`font-semibold text-sm ${style.textColor}`} data-testid="banner-title">
            {banner.title}
          </p>
        )}
        {banner.message && (
          <p className={`text-sm mt-0.5 ${style.textColor} opacity-90`} data-testid="banner-message">
            {banner.message}
          </p>
        )}

        {banner.cta_label && banner.cta_url && (
          <a
            href={banner.cta_url}
            target={banner.cta_url.startsWith('http') ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 mt-2 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${style.btnColor}`}
            data-testid="banner-cta"
          >
            {banner.cta_label}
            {banner.cta_url.startsWith('http') && <ExternalLink className="h-3 w-3" />}
          </a>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded-md hover:bg-black/5 transition-colors"
        aria-label="Dismiss notice"
        data-testid="banner-dismiss"
      >
        <X className="h-4 w-4 text-slate-500" />
      </button>
    </div>
  );
}
